import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceQuestionBuilder } from '../apiUtils';

// Types
export interface SurveyLinksSettings {
  askEmail: boolean;
  autoApproveOnComplete: boolean;
  avQualityCheck: boolean;
  hideTakeSurveyButton: boolean;
  respondentIdAsOthers: boolean;
  allowOnlyExistingUsers: boolean;
}

export interface SurveyLinksLanguage {
  languageId: string;
  displayName: string;
}

export interface SurveyLinksData {
  webUrl: string | null;
  mobileUrl: string | null;
  qrCodeUrl: string | null;
  status: 'live' | 'Closed';
  endDate: number | null;
  settings: SurveyLinksSettings;
  languages: SurveyLinksLanguage[];
}

export interface SurveyLinksSection {
  sectionId: number;
  sectionOrder: number;
  sectionName: string;
  url: string;
  isOn: boolean;
  // Section-link validity window. Nulls = analyst hasn't scheduled
  // this section — link is always active. When set, `startAt`/`endAt`
  // are UTC ISO 8601; `timezone` is the IANA zone the analyst picked
  // in and is kept for re-display (backend enforces on the UTC times).
  startAt: string | null;
  endAt: string | null;
  timezone: string | null;
}

export interface SurveyLinksQuestion {
  sectionOrder: number;
  questionOrder: number;
  questionText: string;
  questionId: number;
  url: string;
}

export interface SurveyLinksPanelData {
  panel: {
    externalPanelId: number | null;
    externalPanelDetails: any | null;
  };
  externalPanels: any[];
  selectedExternalPanel: {
    id: number;
    details: any;
  } | null;
}

// Tier 1: Critical path - Load on page open
export const getSurveyLinksData = createAsyncThunk(
  'surveylinks/getLinks',
  async (batchId: number) => {
    const res = await apiServiceInstanceQuestionBuilder.post(
      '/surveylinks/links',
      { batchId },
    );
    return res.data;
  },
);

// Tier 2a: Lazy load - When Section Links tab clicked
export const getSurveyLinksSections = createAsyncThunk(
  'surveylinks/getSections',
  async (batchId: number) => {
    const res = await apiServiceInstanceQuestionBuilder.post(
      '/surveylinks/sections',
      { batchId },
    );
    return res.data;
  },
);

// Tier 2b: Lazy load - When Embed & API tab clicked
export const getSurveyLinksQuestions = createAsyncThunk(
  'surveylinks/getQuestions',
  async (batchId: number) => {
    const res = await apiServiceInstanceQuestionBuilder.post(
      '/surveylinks/questions',
      { batchId },
    );
    return res.data;
  },
);

// Tier 2c: Lazy load - When Panel tab clicked
export const getSurveyLinksPanels = createAsyncThunk(
  'surveylinks/getPanels',
  async (batchId: number) => {
    const res = await apiServiceInstanceQuestionBuilder.post(
      '/surveylinks/panels',
      { batchId },
    );
    return res.data;
  },
);

// Update settings
export const updateSurveyLinksSettings = async (
  batchId: number,
  projectId: number,
  settings: Partial<SurveyLinksSettings>,
) => {
  const res = await apiServiceInstanceQuestionBuilder.put('/batch/', {
    batchId,
    projectId,
    ...settings,
  });
  return res.data;
};

// Section update — same PUT /question/section endpoint that used to
// only carry `isOn`. Widened to accept the section-link validity
// window fields (all optional). Fields omitted from the payload aren't
// touched on the server, matching the sparse-update pattern in the
// backend query layer. Empty-string values NULL-clear the column —
// used by the analyst UI to unset a previously configured window.
export interface UpdateSectionPayload {
  batchId: number;
  sectionId: number;
  isOn?: boolean;
  linkStartAt?: string;
  linkEndAt?: string;
  linkTimezone?: string;
}

export const updateSection = async (payload: UpdateSectionPayload) => {
  const { batchId, sectionId, ...rest } = payload;
  const body: Record<string, unknown> = { batchId, sectionId };
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined) body[k] = v;
  }
  const res = await apiServiceInstanceQuestionBuilder.put(
    '/question/section',
    body,
  );
  return res.data;
};

// Analyst-initiated Send from the Email & Messages wizard. The backend
// renders the message + button into HTML and ships to Resend's batch
// endpoint. Returns { status, sent, failed } so the UI can toast a
// partial-success outcome distinctly from a full-success one.
export interface SendEmailPayload {
  batchId: number;
  recipients: string[];
  subject: string;
  message: string;
  buttonLabel: string;
  buttonTarget: string;
  // Required. Respondents' replies to the email go to this address.
  replyTo: string;
  // Optional researcher name; the sender shows as "<name> via Enumerate.ai".
  senderName?: string;
  // When true (default on the composer), the button URL gets `userId=<id>`
  // prepended per recipient so responses tie back to the invited row.
  // When false, every recipient gets the plain analyst-picked URL.
  personalizeLink?: boolean;
}

export const sendEmail = async (payload: SendEmailPayload) => {
  const res = await apiServiceInstanceQuestionBuilder.post(
    '/surveylinks/send_email',
    payload,
  );
  return res.data as { status: 'success' | 'partial'; sent: number; failed: number };
};

